/**
 * CART NIT VALIDATION
 */

(function () {
  'use strict';

  // ─── Constants ────────────────────────────────────────────────────────────
  const NIT_PATTERN  = /^[\d-]+[kK]?$/;
  const CF_VALUE     = 'CF';
  const FIELD_NAME   = 'attributes[Número de NIT del cliente]';

  /**
   * Read NIT config from the nearest [data-nit-threshold] wrapper inside
   * the given form, or fall back to any on the page, then to hardcoded defaults.
   * @param {HTMLFormElement} form
   * @returns {{ thresholdCents: number, errorRequired: string, errorFormat: string,
   *             dialogTitle: string, dialogDesc: string, dialogConfirm: string, dialogCancel: string }}
   */
  function getNitConfig(form) {
    var el = (form && form.querySelector('[data-nit-threshold]'))
          || document.querySelector('[data-nit-threshold]');
    if (el) {
      return {
        thresholdCents: parseInt(el.dataset.nitThreshold, 10) || 250000,
        errorRequired:  el.dataset.nitErrorRequired  || 'Se requiere número de NIT para continuar con su compra.',
        errorFormat:    el.dataset.nitErrorFormat    || 'El NIT solo puede contener números y guiones (ej. 1234-56789-101-1).',
        dialogTitle:    el.dataset.nitDialogTitle    || 'Consumidor Final',
        dialogDesc:     el.dataset.nitDialogDesc     || '¿Desea continuar su compra como Consumidor Final?',
        dialogConfirm:  el.dataset.nitDialogConfirm  || 'Sí, continuar',
        dialogCancel:   el.dataset.nitDialogCancel   || 'No, ingresar NIT'
      };
    }
    return {
      thresholdCents: 250000,
      errorRequired:  'Se requiere número de NIT para continuar con su compra.',
      errorFormat:    'El NIT solo puede contener números y guiones (ej. 1234-56789-101-1).',
      dialogTitle:    'Consumidor Final',
      dialogDesc:     '¿Desea continuar su compra como Consumidor Final?',
      dialogConfirm:  'Sí, continuar',
      dialogCancel:   'No, ingresar NIT'
    };
  }

  // ─── Subtotal helpers ─────────────────────────────────────────────────────

  /**
   * Read the raw subtotal in cents from the nearest [data-cart-subtotal]
   * element inside the given form, or fall back to any on the page.
   * Returns 0 if not found.
   * @param {HTMLFormElement} form
   * @returns {number}
   */
  function getSubtotalCents(form) {
    var el = form.querySelector('[data-subtotal]');

    // The raw cents value lives on the cart__items wrapper, not the subtotal
    // display element. Look for it on the page.
    var itemsEl = document.querySelector('[data-cart-subtotal]');
    if (itemsEl) {
      var raw = parseInt(itemsEl.dataset.cartSubtotal, 10);
      if (!isNaN(raw)) return raw;
    }

    // Fallback: try to parse the formatted text inside [data-subtotal].
    // Strip everything except digits and dots/commas, then parse.
    if (el) {
      var text = el.textContent.replace(/[^\d.,]/g, '').replace(',', '.');
      var parsed = parseFloat(text);
      if (!isNaN(parsed)) {
        // Assume the displayed value is already in the local currency unit (Q),
        // so multiply by 100 to get cents.
        return Math.round(parsed * 100);
      }
    }

    return 0;
  }

  // ─── Error / hint UI ──────────────────────────────────────────────────────

  function showError(input, message) {
    var errorEl = document.getElementById(input.id + '-error');
    input.setAttribute('aria-invalid', 'true');
    input.classList.add('cart__nit-input--error');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.removeAttribute('hidden');
    }
    input.focus();
  }

  function clearError(input) {
    var errorEl = document.getElementById(input.id + '-error');
    input.removeAttribute('aria-invalid');
    input.classList.remove('cart__nit-input--error');
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.setAttribute('hidden', '');
    }
  }

  // ─── Button loading state ─────────────────────────────────────────────────

  function resetCheckoutBtn(form) {
    var btn = form.querySelector('[name="checkout"]');
    if (btn) {
      requestAnimationFrame(function () {
        btn.classList.remove('btn--loading');
      });
    }
  }

  // ─── Confirmation dialog ──────────────────────────────────────────────────

  /**
   * Show a branded confirmation dialog.
   * Returns a Promise that resolves to true (Yes) or false (No).
   * @param {{ dialogTitle: string, dialogDesc: string, dialogConfirm: string, dialogCancel: string }} cfg
   * @returns {Promise<boolean>}
   */
  function showConfirmDialog(cfg) {
    return new Promise(function (resolve) {
      // Remove any stale dialog
      var existing = document.getElementById('nit-confirm-dialog');
      if (existing) existing.remove();

      var overlay = document.createElement('div');
      overlay.id = 'nit-confirm-dialog';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-labelledby', 'nit-dialog-title');
      overlay.setAttribute('aria-describedby', 'nit-dialog-desc');
      overlay.innerHTML = [
        '<div class="nit-dialog__backdrop"></div>',
        '<div class="nit-dialog__box" tabindex="-1">',
        '  <p id="nit-dialog-title" class="nit-dialog__title">' + cfg.dialogTitle + '</p>',
        '  <p id="nit-dialog-desc" class="nit-dialog__desc">',
        '    ' + cfg.dialogDesc,
        '  </p>',
        '  <div class="nit-dialog__actions">',
        '    <button type="button" class="btn nit-dialog__btn--yes" id="nit-dialog-yes">' + cfg.dialogConfirm + '</button>',
        '    <button type="button" class="btn btn--secondary nit-dialog__btn--no" id="nit-dialog-no">' + cfg.dialogCancel + '</button>',
        '  </div>',
        '</div>'
      ].join('');

      document.body.appendChild(overlay);

      // Animate in
      requestAnimationFrame(function () {
        overlay.classList.add('nit-dialog--open');
      });

      var box = overlay.querySelector('.nit-dialog__box');
      box.focus();

      function close(result) {
        overlay.classList.remove('nit-dialog--open');
        overlay.classList.add('nit-dialog--closing');
        setTimeout(function () {
          overlay.remove();
          resolve(result);
        }, 250);
      }

      document.getElementById('nit-dialog-yes').addEventListener('click', function () { close(true); });
      document.getElementById('nit-dialog-no').addEventListener('click',  function () { close(false); });

      // Close on backdrop click
      overlay.querySelector('.nit-dialog__backdrop').addEventListener('click', function () { close(false); });

      // Close on Escape
      overlay.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') close(false);
      });
    });
  }

  // ─── Core validation ──────────────────────────────────────────────────────

  /**
   * Validate the NIT field for the given form.
   * Returns a Promise<boolean>: true = proceed, false = block.
   * @param {HTMLFormElement} form
   * @returns {Promise<boolean>}
   */
  function validateNit(form) {
    var input = form.querySelector('[name="' + FIELD_NAME + '"]');
    if (!input) return Promise.resolve(true);

    var cfg          = getNitConfig(form);
    var value        = input.value.trim().toUpperCase();
    var subtotal     = getSubtotalCents(form);
    var aboveLimit   = subtotal >= cfg.thresholdCents;

    // ── Above threshold ───────────────────────────────────────────────────
    if (aboveLimit) {
      if (value === '' || value === CF_VALUE) {
        showError(input, cfg.errorRequired);
        return Promise.resolve(false);
      }
      if (!NIT_PATTERN.test(value)) {
        showError(input, cfg.errorFormat);
        return Promise.resolve(false);
      }
      clearError(input);
      return Promise.resolve(true);
    }

    // ── Below threshold ───────────────────────────────────────────────────

    // CF is explicitly valid below the threshold
    if (value === CF_VALUE) {
      clearError(input);
      return Promise.resolve(true);
    }

    // Non-empty but invalid format
    if (value !== '' && !NIT_PATTERN.test(value)) {
      showError(input, cfg.errorFormat);
      return Promise.resolve(false);
    }

    // Valid NIT
    if (value !== '') {
      clearError(input);
      return Promise.resolve(true);
    }

    // Empty field below threshold → ask if Final Consumer
    clearError(input);
    return showConfirmDialog(cfg).then(function (confirmed) {
      if (confirmed) {
        // Write "CF" into the field so it is submitted with the order
        input.value = CF_VALUE;
        return true;
      }
      input.focus();
      return false;
    });
  }

  // ─── Clear NIT attribute after checkout ───────────────────────────────────

  /**
   * Clear the NIT cart attribute via the Shopify Cart API.
   * Called just before the page navigates to checkout so the field is
   * empty when the customer returns or starts a new cart.
   */
  function clearNitAttribute() {
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ attributes: { 'Número de NIT del cliente': '' } })
      });
    } catch (e) {
      // Non-critical — silently ignore
    }
  }

  /**
   * Persist a NIT value to the cart attribute via the Shopify Cart API.
   * Returns a Promise that resolves when the update is complete (or fails silently).
   * @param {string} value
   * @returns {Promise<void>}
   */
  function saveNitAttribute(value) {
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ attributes: { 'Número de NIT del cliente': value } })
    }).then(function () {}).catch(function () {});
  }

  // ─── Form wiring ──────────────────────────────────────────────────────────

  function attachValidation(form) {
    if (!form || form.dataset.nitAttached) return;
    form.dataset.nitAttached = '1';

    function onSubmit(event) {
      var submitter  = event.submitter;
      var isCheckout = submitter && submitter.name === 'checkout';
      if (!isCheckout) return;

      // Always prevent immediately; we'll navigate programmatically if valid.
      event.preventDefault();
      event.stopImmediatePropagation();

      resetCheckoutBtn(form);

      validateNit(form).then(function (valid) {
        if (!valid) return;

        var nitInput = form.querySelector('[name="' + FIELD_NAME + '"]');
        var nitValue = nitInput ? nitInput.value.trim().toUpperCase() : '';

        // Persist the NIT value (e.g. "CF") to the cart attribute, then
        // navigate directly to /checkout — bypassing all theme AJAX wiring.
        saveNitAttribute(nitValue).then(function () {
          window.location.href = '/checkout';
        });
      });
    }

    // Capture phase so we run before the theme's own submit handlers.
    form.addEventListener('submit', onSubmit, true);

    // Live: clear error while the user is typing
    var input = form.querySelector('[name="' + FIELD_NAME + '"]');
    if (input) {
      input.addEventListener('input', function () {
        clearError(input);
      });
    }
  }

  // ─── Clear NIT when first item is added to an empty cart ─────────────────

  /**
   * Wipe the NIT input fields in the DOM immediately (no flicker).
   */
  function clearNitInputs() {
    document.querySelectorAll('[name="' + FIELD_NAME + '"]').forEach(function (el) {
      el.value = '';
      clearError(el);
    });
  }

  /**
   * Listen for the theme's ajaxProduct:added event.
   * After each add, fetch the live cart. If item_count === 1 the cart was
   * empty before this add — clear the stored NIT attribute and the inputs.
   */
  document.addEventListener('ajaxProduct:added', function () {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart.item_count === 1) {
          saveNitAttribute('');
          clearNitInputs();
        }
      })
      .catch(function () {});
  });

  // ─── Init & re-init ───────────────────────────────────────────────────────

  function init() {
    // Reset attachment flags so re-init after AJAX rebuilds works cleanly
    ['CartPageForm', 'CartDrawerForm'].forEach(function (id) {
      var form = document.getElementById(id);
      if (form) delete form.dataset.nitAttached;
    });
    attachValidation(document.getElementById('CartPageForm'));
    attachValidation(document.getElementById('CartDrawerForm'));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('cart:updated', init);

  // MutationObserver fallback for drawer re-renders
  var drawerObserver = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].addedNodes.length) {
        var drawerForm = document.getElementById('CartDrawerForm');
        if (drawerForm && !drawerForm.dataset.nitAttached) {
          attachValidation(drawerForm);
        }
      }
    }
  });

  var cartDrawer = document.getElementById('CartDrawer');
  if (cartDrawer) {
    drawerObserver.observe(cartDrawer, { childList: true, subtree: true });
  }

})();
